/**
 * LAW 59 (18 October 2026) · THE SKETCH PAD. OWNER: web-frontend-engineer.
 *
 * The owner: "make it so paul can write using apple pen and sketch if needed."
 * Paul measures a man, then draws on the ticket for the garment: a shoulder, a
 * pocket set lower than the house's, a word for the cutter. This is that pad.
 *
 * ── STROKES, NOT PIXELS ───────────────────────────────────────────────────
 * A drawing is kept as strokes, each point `[x, y, pressure]` in 0..1 of the
 * pad, which is what the server stores (append-only, one drawing a garment an
 * order) and what this pad redraws from on mount and whenever `drawing` changes
 * (a saved sketch arriving from the server). The FRAME is the pad's size in CSS
 * pixels when the first stroke went down; a pad of another size draws the same
 * frame scaled evenly and centred, so Paul's drawing is never stretched.
 *
 * ── THE PENCIL ALWAYS DRAWS; A FINGER SCROLLS ─────────────────────────────
 * Pointer events only. A pen draws, a mouse draws, and a finger draws only when
 * the finger toggle is on. With it off the canvas carries `touch-action: pan-y`
 * so a finger moves the page, and a non-passive touch listener holds the page
 * still under the pencil: iPadOS lets an Apple Pencil scroll a page too, and a
 * vertical pencil stroke would otherwise be taken for a scroll. A pen arriving
 * while a finger is mid-stroke takes over, because that finger was a palm.
 *
 * ── NO NETWORK ────────────────────────────────────────────────────────────
 * The pad asks to be saved (`onSave`) and is told what the server kept
 * (`saved`); the screen owns the call. It holds nothing of the drawing beyond
 * the one stroke under the pencil.
 *
 * ── ON PAPER ──────────────────────────────────────────────────────────────
 * The canvas prints as drawn. Its backing store is never under two pixels per
 * CSS pixel, so a ticket printed from a desk is as sharp as one printed from
 * the iPad, and it carries explicit `width` and `height` attributes, which is
 * what Safari needs to print a canvas at all. The controls and the status line
 * are `.pt-noprint`.
 *
 * The numbers below are LAW 59's brief (the line, the pressure range, the
 * aspect, the minimum height), named once here. They are drawing behaviour,
 * not style: the pad's look is in components.css, from the tokens. What the
 * server accepts of a drawing is not named here: it is `SKETCH_LIMITS`, from
 * @pt/core, the one table the server refuses by.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import { SKETCH_LIMITS } from '@pt/core';
import { t } from '@pt/copy';
import { color } from '@pt/tokens';
import { dateTimeInWords } from '@pt/view';
import type { SketchDrawing, SketchStroke, TicketSketch } from '../lib/contracts.ts';
import { Button, SelectionMark } from './primitives.tsx';

/** The line's width in CSS pixels of the pad; pressure takes it from 0.6 to 1.6 times this. */
export const SKETCH_LINE = 2.2;
const PRESSURE_FLOOR = 0.6;
/** What a pointer that reports no pressure (a finger, some mice) is taken to press with. */
const PRESSURE_UNKNOWN = 0.5;
/** Width to height. A garment's drawing is a landscape strip under its ticket. */
const ASPECT = 2;
const MIN_HEIGHT = 200;
/** Before the pad has measured its column: a size of the right shape, and nothing more. */
const UNMEASURED_WIDTH = 800;
/** A point nearer than this (CSS px) to the last one kept adds nothing the eye can see. */
const MIN_STEP = 0.75;
/** A zero-length line is pruned by the canvas, so a dot is drawn as a hair's length. */
const DOT = 0.01;

type Point = readonly [number, number, number];

/** What drawing needs of a 2D context, and no more: a test hands in a recorder of the same shape. */
export type SketchContext = Pick<
  CanvasRenderingContext2D,
  'beginPath' | 'moveTo' | 'lineTo' | 'stroke' | 'lineWidth' | 'lineCap' | 'lineJoin' | 'strokeStyle'
>;

/** A drawing with nothing on it, its frame the pad's size in whole CSS pixels. */
export function emptyDrawing(width: number, height: number): SketchDrawing {
  return { width: Math.round(width), height: Math.round(height), strokes: [] };
}

/** Where a drawing's frame sits on a pad of this size: scaled evenly, centred, never stretched. */
interface Frame {
  readonly scale: number;
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly height: number;
}

function frameOf(drawing: Pick<SketchDrawing, 'width' | 'height'>, width: number, height: number): Frame {
  const across = drawing.width > 0 ? drawing.width : width;
  const down = drawing.height > 0 ? drawing.height : height;
  const scale = Math.min(width / across, height / down);
  return {
    scale,
    left: (width - across * scale) / 2,
    top: (height - down * scale) / 2,
    width: across * scale,
    height: down * scale,
  };
}

/** One piece of a stroke, from the point before to this one, at the width their pressure gives. */
function segment(ctx: SketchContext, frame: Frame, w: number, before: Point | undefined, at: Point | undefined): void {
  if (at === undefined) return;
  const from = before ?? at;
  const x0 = frame.left + from[0] * frame.width;
  const y0 = frame.top + from[1] * frame.height;
  const x1 = frame.left + at[0] * frame.width;
  const y1 = frame.top + at[1] * frame.height;
  ctx.beginPath();
  ctx.lineWidth = w * frame.scale * (PRESSURE_FLOOR + (from[2] + at[2]) / 2);
  ctx.moveTo(x0, y0);
  ctx.lineTo(x0 === x1 && y0 === y1 ? x1 + DOT : x1, y1);
  ctx.stroke();
}

/**
 * Draw a drawing onto a context sized `width` by `height` CSS pixels. One
 * `moveTo`, one `lineTo` and one `stroke` a point: each point ends the piece of
 * line that reaches it, and a stroke's first point is a dot, which is also what
 * a single tap of the pencil leaves. Round caps and joins, so the pieces meet
 * without a seam. Clearing is the caller's.
 */
export function drawStrokes(
  ctx: SketchContext,
  drawing: SketchDrawing,
  width: number,
  height: number,
  ink?: string,
): void {
  const frame = frameOf(drawing, width, height);
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  if (ink !== undefined) ctx.strokeStyle = ink;
  for (const stroke of drawing.strokes) {
    for (let index = 0; index < stroke.p.length; index += 1) {
      segment(ctx, frame, stroke.w, stroke.p[index - 1], stroke.p[index]);
    }
  }
}

/** Whether two lists of strokes draw the same thing: the same strokes, or the same strokes back off the wire. */
export function sameStrokes(a: readonly SketchStroke[], b: readonly SketchStroke[]): boolean {
  return a === b || JSON.stringify(a) === JSON.stringify(b);
}

/**
 * The one line under the pad, in words. Read-only: who drew what the server
 * kept, or that nothing is drawn. Drawable: where the save stands (going,
 * refused, not yet asked for, done), or that nothing is drawn.
 */
function statusLine({
  drawable,
  saved,
  saving,
  failed,
  dirty,
  full,
}: {
  drawable: boolean;
  saved: TicketSketch | null;
  saving: boolean;
  failed: boolean;
  dirty: boolean;
  full: boolean;
}): string {
  const by = (sketch: TicketSketch) => ({ authorName: sketch.drawnByName, when: dateTimeInWords(sketch.drawnAt) });
  if (!drawable) {
    if (saved === null || saved.strokes.length === 0) return t('tickets.sketch.empty');
    return t('tickets.sketch.readOnly', by(saved));
  }
  if (saving) return t('tickets.sketch.saving');
  if (failed) return t('tickets.sketch.failed');
  if (full) return t('tickets.sketch.full');
  if (dirty) return t('tickets.sketch.unsaved');
  if (saved === null) return t('tickets.sketch.empty');
  return t('tickets.sketch.saved', by(saved));
}

/** The house ink, as the page resolves it, so the drawing is the same black as the type beside it. */
function inkOf(element: Element): string {
  const value = getComputedStyle(element).getPropertyValue('--color-ink-primary').trim();
  return value === '' ? color.ink.primary : value;
}

/** Pixels in the backing store a CSS pixel: the screen's own, never under 2 (paper), never over 3 (memory). */
function backingRatio(): number {
  const screen = typeof window === 'undefined' ? 1 : window.devicePixelRatio || 1;
  return Math.min(3, Math.max(2, screen));
}

const clampUnit = (value: number): number => (value < 0 ? 0 : value > 1 ? 1 : value);
const rounded = (value: number, places: number): number => {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
};

/** The stroke under the pencil, kept outside React state: it is drawn as it arrives, not rendered. */
interface Live {
  readonly pointerId: number;
  readonly pointerType: string;
  /** The drawing whose frame this stroke is drawn into. */
  readonly frame: Pick<SketchDrawing, 'width' | 'height'>;
  readonly place: Frame;
  readonly rect: { readonly left: number; readonly top: number };
  readonly w: number;
  readonly points: Point[];
  /** The points this stroke may still take before the drawing is at the server's limits. */
  readonly room: number;
}

export interface SketchPadProps {
  /** The drawing as it stands, saved or not. The pad redraws from it whenever it changes. */
  readonly drawing: SketchDrawing | null;
  /**
   * A stroke finished, an undo or a clear: the drawing as it now stands.
   * Without it the pad is the workshop's view: no controls, no pointer
   * handling, and the page scrolls over it.
   */
  readonly onChange?: (drawing: SketchDrawing) => void;
  /** The canvas's accessible name. */
  readonly label: string;
  /** The drawing as the server last kept it: who drew it, and when. */
  readonly saved?: TicketSketch | null;
  /** Ask the screen to save the drawing as it stands. Without it there is no Save control. */
  readonly onSave?: () => void;
  readonly saving?: boolean;
  /** The last save was refused or never arrived. */
  readonly failed?: boolean;
}

export function SketchPad({
  drawing,
  onChange,
  label,
  saved = null,
  onSave,
  saving = false,
  failed = false,
}: SketchPadProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ width: UNMEASURED_WIDTH, height: UNMEASURED_WIDTH / ASPECT });
  const [ratio, setRatio] = useState(backingRatio);
  /* Off when the pad appears: a finger scrolls the page until he says otherwise. */
  const [finger, setFinger] = useState(false);
  /* What the last Clear took away, in its own frame. Undo on an empty pad puts
     it back, so a tap on Clear beside Undo is never the end of a drawing. */
  const [cleared, setCleared] = useState<SketchDrawing | null>(null);
  const live = useRef<Live | null>(null);
  /** What the canvas shows now, so a drawing this pad has just committed is not painted twice. */
  const shown = useRef<{ drawing: SketchDrawing | null; width: number; height: number; ratio: number } | null>(
    null,
  );

  const strokes = drawing?.strokes ?? [];
  const drawable = onChange !== undefined;
  /* How many more points the pad may take before the server would refuse the drawing: the point
     cap, or the byte cap read as points (a point serialises in under 23 bytes, a stroke's envelope
     in under 24), whichever comes first. A pad that let a drawing grow past this would fail to save
     every time and say only "try again". */
  const held = strokes.reduce((n, stroke) => n + stroke.p.length, 0);
  const pointRoom = Math.max(
    0,
    Math.min(SKETCH_LIMITS.points, Math.floor((SKETCH_LIMITS.bytes - 64 - strokes.length * 24) / 23)) - held,
  );
  const full = pointRoom === 0 || strokes.length >= SKETCH_LIMITS.strokes;

  const repaint = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d') ?? null;
    if (canvas === null || ctx === null) return;
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.clearRect(0, 0, size.width, size.height);
    drawStrokes(ctx, drawing ?? emptyDrawing(size.width, size.height), size.width, size.height, inkOf(canvas));
    const current = live.current;
    if (current !== null) {
      for (let index = 0; index < current.points.length; index += 1) {
        segment(ctx, current.place, current.w, current.points[index - 1], current.points[index]);
      }
    }
    shown.current = { drawing, width: size.width, height: size.height, ratio };
  };

  /* Sized to its column at two to one, never under the minimum height, and
     measured again whenever the column changes (an iPad turned on its side). */
  useLayoutEffect(() => {
    const host = hostRef.current;
    if (host === null) return undefined;
    const measure = () => {
      const width = host.clientWidth;
      if (width <= 0) return;
      const height = Math.max(MIN_HEIGHT, Math.round(width / ASPECT));
      setSize((was) => (was.width === width && was.height === height ? was : { width, height }));
      setRatio(backingRatio());
    };
    measure();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  /* Redrawn from `drawing` on mount, when it changes, and after a resize (a
     new backing store is a blank one). Not when the pad itself has just put
     the same drawing on the glass. */
  useLayoutEffect(() => {
    const was = shown.current;
    if (
      was !== null &&
      was.drawing === drawing &&
      was.width === size.width &&
      was.height === size.height &&
      was.ratio === ratio
    ) {
      return;
    }
    repaint();
  });

  /* The page held still under the pencil, and under a palm while a stroke is
     going down. React's own touch listeners are passive and cannot do this. */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null || !drawable) return undefined;
    const hold = (event: TouchEvent) => {
      const stylus = Array.from(event.touches).some(
        (touch) => (touch as Touch & { touchType?: string }).touchType === 'stylus',
      );
      if (stylus || live.current !== null) event.preventDefault();
    };
    canvas.addEventListener('touchstart', hold, { passive: false });
    canvas.addEventListener('touchmove', hold, { passive: false });
    return () => {
      canvas.removeEventListener('touchstart', hold);
      canvas.removeEventListener('touchmove', hold);
    };
  }, [drawable]);

  const pointOf = (current: Live, event: PointerEvent): Point => {
    const x = (event.clientX - current.rect.left - current.place.left) / current.place.width;
    const y = (event.clientY - current.rect.top - current.place.top) / current.place.height;
    const pressure = event.pressure > 0 ? event.pressure : PRESSURE_UNKNOWN;
    return [rounded(clampUnit(x), 4), rounded(clampUnit(y), 4), rounded(clampUnit(pressure), 2)];
  };

  /** Every sample the pointer gave since the last event (an Apple Pencil reports far more often than the screen draws). */
  const extend = (native: PointerEvent) => {
    const current = live.current;
    if (current === null) return;
    const coalesced = typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const ctx = canvasRef.current?.getContext('2d') ?? null;
    for (const sample of coalesced.length > 0 ? coalesced : [native]) {
      const point = pointOf(current, sample);
      const last = current.points[current.points.length - 1];
      if (
        last !== undefined &&
        Math.hypot((point[0] - last[0]) * current.place.width, (point[1] - last[1]) * current.place.height) <
          MIN_STEP
      ) {
        continue;
      }
      if (current.points.length >= current.room) break;   // the pad is full; the status line says so
      current.points.push(point);
      if (ctx !== null) segment(ctx, current.place, current.w, last, point);
    }
  };

  const begin = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (full) return;
    const type = event.pointerType;
    if (type === 'touch' && !finger) return; // the page scrolls
    if (type === 'mouse' && event.button !== 0) return;
    const current = live.current;
    if (current !== null) {
      // One hand at a time, except that a pencil outranks a finger: that finger was a palm.
      if (!(type === 'pen' && current.pointerType === 'touch')) return;
      live.current = null;
      repaint();
    }
    const canvas = event.currentTarget;
    event.preventDefault();
    try {
      canvas.setPointerCapture(event.pointerId);
    } catch {
      /* the pointer is already gone; the stroke ends on its own */
    }
    const rect = canvas.getBoundingClientRect();
    const frame = drawing !== null && strokes.length > 0 ? drawing : emptyDrawing(rect.width, rect.height);
    const place = frameOf(frame, rect.width, rect.height);
    live.current = {
      pointerId: event.pointerId,
      pointerType: type,
      frame: { width: frame.width, height: frame.height },
      place,
      rect: { left: rect.left, top: rect.top },
      w: Math.min(SKETCH_LIMITS.widthMax, Math.max(SKETCH_LIMITS.widthMin, rounded(SKETCH_LINE / place.scale, 3))),
      points: [],
      room: pointRoom,
    };
    extend(event.nativeEvent);
  };

  const move = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (live.current?.pointerId !== event.pointerId) return;
    extend(event.nativeEvent);
  };

  /**
   * The stroke ends: kept (lifted, or a pen or mouse the browser took away) or
   * dropped (a finger taken for a scroll). Only a lift carries a position worth
   * keeping; a cancelled or lost pointer's coordinates are not a place on the pad.
   */
  const finish = (event: ReactPointerEvent<HTMLCanvasElement>, keep: boolean, lifted: boolean) => {
    const current = live.current;
    if (current === null || current.pointerId !== event.pointerId) return;
    if (keep && lifted) extend(event.nativeEvent);
    live.current = null;
    if (!keep || current.points.length === 0 || onChange === undefined) {
      repaint();
      return;
    }
    const next: SketchDrawing = {
      width: current.frame.width,
      height: current.frame.height,
      strokes: [...strokes, { w: current.w, p: current.points }],
    };
    shown.current = { drawing: next, width: size.width, height: size.height, ratio };
    onChange(next);
  };

  const undo = () => {
    if (onChange === undefined) return;
    if (drawing !== null && strokes.length > 0) {
      onChange({ width: drawing.width, height: drawing.height, strokes: strokes.slice(0, -1) });
      return;
    }
    if (cleared !== null) {
      onChange(cleared);
      setCleared(null);
    }
  };

  const clear = () => {
    if (onChange === undefined || drawing === null || strokes.length === 0) return;
    setCleared({ width: drawing.width, height: drawing.height, strokes });
    onChange({ width: drawing.width, height: drawing.height, strokes: [] });
  };

  const dirty = !sameStrokes(strokes, saved?.strokes ?? []);

  return (
    <div className="pt-sketchpad">
      {drawable ? (
        <div className="pt-sketchpad__controls pt-noprint">
          {/* The label does not change with the state (a toggle's name is its
              act; `aria-pressed` and the mark carry whether it is on). */}
          <button
            type="button"
            className="pt-sketchpad__finger"
            aria-pressed={finger}
            onClick={() => setFinger((on) => !on)}
          >
            <SelectionMark checked={finger} />
            <span>{t('tickets.sketch.finger.on')}</span>
          </button>
          <Button onClick={undo} unavailable={strokes.length === 0 && cleared === null}>
            {t('tickets.sketch.undo')}
          </Button>
          <Button onClick={clear} unavailable={strokes.length === 0}>
            {t('tickets.sketch.clear')}
          </Button>
          {onSave === undefined ? null : (
            <Button variant="primary" onClick={onSave} busy={saving} unavailable={!dirty}>
              {t('tickets.sketch.save')}
            </Button>
          )}
        </div>
      ) : null}
      <div ref={hostRef} className="pt-sketchpad__surface">
        <canvas
          ref={canvasRef}
          className={drawable ? 'pt-sketchpad__canvas pt-sketchpad__canvas--draw' : 'pt-sketchpad__canvas'}
          width={Math.round(size.width * ratio)}
          height={Math.round(size.height * ratio)}
          role="img"
          aria-label={label}
          {...(drawable
            ? {
                style: { touchAction: finger ? 'none' : 'pan-y' },
                onPointerDown: begin,
                onPointerMove: move,
                onPointerUp: (event: ReactPointerEvent<HTMLCanvasElement>) => finish(event, true, true),
                onPointerCancel: (event: ReactPointerEvent<HTMLCanvasElement>) =>
                  finish(event, event.pointerType !== 'touch', false),
                onLostPointerCapture: (event: ReactPointerEvent<HTMLCanvasElement>) => finish(event, true, false),
              }
            : {})}
        />
      </div>
      <p className="pt-sketchpad__status pt-noprint">{statusLine({ drawable, saved, saving, failed, dirty, full })}</p>
    </div>
  );
}
